import {
    ACCESS_TOKEN_MAX_AGE_SECOND,
    REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import { datadogRum } from '@datadog/browser-rum';
import { SignInResponse } from '@models/auth';
import axios, {
    AxiosError,
    AxiosInstance, AxiosRequestConfig, AxiosRequestTransformer, AxiosResponse, InternalAxiosRequestConfig,
} from 'axios';
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';

import refreshState from './refreshState';

const cookies = new Cookies(null, { path: '/' });

export interface HttpClient extends AxiosInstance {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

const isPlainObject = (v: unknown) => Object.prototype.toString.call(v) === '[object Object]';

const dateTransformer: AxiosRequestTransformer = (data: any) => {
    if (data instanceof Date) {
        return dayjs(data).toISOString();
    }
    if (Array.isArray(data)) {
    // @ts-ignore
        return data.map((val: any) => dateTransformer(val));
    }
    if (isPlainObject(data)) {
        return Object.fromEntries(
            // @ts-ignore
            Object.entries(data).map(([key, val]) => [key, dateTransformer(val)]),
        );
    }
    return data;
};

const instance: HttpClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true,
    // @ts-ignore
    transformRequest: [dateTransformer].concat(axios.defaults.transformRequest),
    paramsSerializer: {
        indexes: null,
    },
});

function reissueToken(refreshToken: string): Promise<AxiosResponse<SignInResponse>> {
    console.log('refresh on instance');
    return axios.post(
        `${import.meta.env.VITE_API_URL}/auth/token/refresh`,
        { refreshToken },
    );
}

let pendingRefresh: Promise<string> | null = null;

function startRefresh(): Promise<string> {
    if (pendingRefresh) return pendingRefresh;

    const refreshToken = cookies.get('refreshToken');
    if (!refreshToken) return Promise.reject(new Error('No refresh token'));

    pendingRefresh = reissueToken(refreshToken)
        .then((res) => {
            const { token, refreshToken: newRefreshToken } = res.data;
            cookies.set('accessToken', token, { path: '/', maxAge: ACCESS_TOKEN_MAX_AGE_SECOND });
            cookies.set('refreshToken', newRefreshToken, { path: '/', maxAge: REFRESH_TOKEN_MAX_AGE_SECOND });
            refreshState.isUnAuthorized = false;
            return token;
        })
        .catch((e) => {
            cookies.remove('accessToken', { path: '/' });
            cookies.remove('refreshToken', { path: '/' });
            console.error('인증 오류 발생', e);
            datadogRum.addAction('refreshTokenFailed', { message: JSON.stringify(e) });
            refreshState.reIssueFailed = true;
            throw e;
        })
        .finally(() => {
            pendingRefresh = null;
        });

    return pendingRefresh;
}

async function handlerRequest(config: InternalAxiosRequestConfig<any>) {
    let projectId = cookies.get('projectId');
    if (!projectId) {
        const params = new URLSearchParams(window.location.search);
        projectId = params.get('projectId');
    }
    config.headers['Waldlust-Project-ID'] = projectId;
    let accessToken = cookies.get('accessToken');
    if ((!accessToken || refreshState.isUnAuthorized) && !refreshState.reIssueFailed) {
        if (cookies.get('refreshToken')) {
            console.log('trying url:', config.url);
            try {
                accessToken = await startRefresh();
            } catch (e) {
                console.error(e);
            }
        }
    }
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    const lang = cookies.get('lang');
    if (lang) {
        config.headers['Accept-Language'] = lang;
    }
    return config;
}

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

instance.interceptors.response.use(
    (res) => res.data,
    async (error: AxiosError) => {
        if (error.response?.status === 401
            // 로그인 오류인 경우 제외
            && !error.config?.url?.includes('/oauth/sign-in')
        && !error.config?.url?.includes('/auth/signin')) {
            const projectId = cookies.get('projectId');
            if (projectId) {
                cookies.remove('accessToken', { path: '/' });
                refreshState.isUnAuthorized = true;

                const originalConfig = error.config as RetryableConfig | undefined;
                if (!refreshState.reIssueFailed && originalConfig && !originalConfig._retry) {
                    originalConfig._retry = true;
                    try {
                        const newToken = await startRefresh();
                        originalConfig.headers.Authorization = `Bearer ${newToken}`;
                    } catch (e) {
                        console.error(e);
                    }
                    return instance(originalConfig);
                }
                if (refreshState.reIssueFailed) {
                    cookies.remove('refreshToken', { path: '/' });
                }
            }
        }
        return Promise.reject(error);
    },
);
instance.interceptors.request.use(handlerRequest);

export default instance;
